// Church billing page. Redesigned around **per-tool selection**: church picks
// a tier ladder (based on directory size) + which tools they want to pay for.
// Total price = sum of each selected tool's price at the chosen tier.
//
// Two flows:
//   • First-time / lapsed churches → "Start subscription" opens a Stripe
//     Checkout Session with one line item per selected tool.
//   • Churches with an active subscription → "Save changes" calls
//     /coord/billing/update-tools, which uses Stripe subscription-item edits
//     (add/remove/change) with proration. No re-checkout round trip.
//
// A minimum of one tool is required — the Save button disables when the
// selection is empty. Existing subscribers who tick everything off get a
// helpful hint instead.

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CreditCard, CheckCircle2, AlertCircle, Sparkles, RefreshCw, ExternalLink,
  BookOpen, Users, Heart, Tablet,
} from "lucide-react";

const STATUS_META = {
  active:         { label: "Active",           color: "var(--olive)",     hint: "Subscription in good standing" },
  grandfathered:  { label: "Grandfathered",    color: "var(--gold)",      hint: "Comped by The Disciple Life — no charges" },
  pending_payment:{ label: "Payment needed",   color: "var(--terracotta)", hint: "Start a Stripe subscription to unlock ongoing access" },
  past_due:       { label: "Past due",         color: "var(--terracotta)", hint: "The last invoice failed — update your card" },
  canceled:       { label: "Canceled",         color: "var(--muted)",     hint: "Subscription ended — restart to keep access" },
};

const TOOL_ICON = { tracker: BookOpen, attendance: Users, prayer: Heart, kiosk: Tablet };

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
}

export default function CrmBilling() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedTools, setSelectedTools] = useState([]);
  const [sp] = useSearchParams();

  const load = () => {
    setLoading(true);
    api.get("/coord/billing")
      .then((r) => {
        setData(r.data);
        setSelectedTier(r.data.plan_tier);
        // Tracker is the required base — always seed it, even if the server
        // returned a legacy list without it, so the UI stays consistent with
        // the write-path normalization.
        const t = new Set(r.data.tools_enabled || []);
        t.add("tracker");
        setSelectedTools(Array.from(t));
      })
      .catch((err) => toast.error(err?.response?.data?.detail || "Couldn't load billing."))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    if (sp.get("checkout") === "success") toast.success("Payment received — welcome aboard!");
    else if (sp.get("checkout") === "canceled") toast.info("Checkout canceled.");
  }, [sp]);

  const totalPrice = useMemo(() => {
    if (!data || !selectedTier) return 0;
    return (selectedTools || []).reduce((sum, tk) => {
      const t = data.tool_catalog.find((x) => x.key === tk);
      return sum + (t?.prices?.[selectedTier] || 0);
    }, 0);
  }, [data, selectedTier, selectedTools]);

  const toggleTool = (key) => {
    // Tracker is required — it can't be toggled off from the billing UI.
    if (data?.tool_catalog?.find((t) => t.key === key)?.required) return;
    setSelectedTools((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const dirty = useMemo(() => {
    if (!data) return false;
    const tierChanged = selectedTier && selectedTier !== data.plan_tier;
    const toolsChanged = JSON.stringify([...(selectedTools || [])].sort()) !== JSON.stringify([...(data.tools_enabled || [])].sort());
    return tierChanged || toolsChanged;
  }, [data, selectedTier, selectedTools]);

  const startCheckout = async () => {
    if (selectedTools.length === 0) { toast.error("Pick at least one tool."); return; }
    setBusy(true);
    try {
      const r = await api.post("/coord/billing/checkout", {
        origin_url: window.location.origin,
        plan_tier: selectedTier,
        tools: selectedTools,
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout.");
    } finally { setBusy(false); }
  };

  const saveChanges = async () => {
    if (selectedTools.length === 0) { toast.error("Pick at least one tool."); return; }
    setBusy(true);
    try {
      const r = await api.post("/coord/billing/update-tools", {
        origin_url: window.location.origin,
        plan_tier: selectedTier,
        tools: selectedTools,
      });
      if (r.data.url) {
        // Fell back to a fresh checkout because there's no active subscription yet.
        window.location.href = r.data.url;
        return;
      }
      toast.success(`Subscription updated — $${r.data.price_usd}/mo effective now.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update.");
    } finally { setBusy(false); }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/coord/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't open billing portal.");
    } finally { setBusy(false); }
  };

  const status = useMemo(() => STATUS_META[data?.billing_status] || STATUS_META.canceled, [data]);
  const overage = data && data.current_members > data.max_members;
  const hasActiveSub = data?.billing_status === "active" || data?.billing_status === "past_due";

  if (loading) return <div className="max-w-4xl mx-auto px-6 py-16" style={{ color: "var(--muted)" }}>Loading…</div>;
  if (!data) return null;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-12 py-10" data-testid="crm-billing-page">
      <header className="mb-8">
        <div className="overline mb-2">Subscription</div>
        <h1 className="font-serif-display text-3xl lg:text-4xl tracking-[-0.03em]" style={{ color: "var(--ink)" }}>
          Church billing
        </h1>
        <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--muted)" }}>
          Pick your tier (directory size) and the tools your church wants to use. Each tool has its own monthly price — you only pay for what you use. Auto-renews every month.
        </p>
      </header>

      {/* Status card */}
      <section className="card-surface p-6 mb-8" data-testid="billing-status-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[0.6rem] uppercase tracking-[0.14em]"
                style={{ background: "var(--surface-hover)", color: status.color, border: `1px solid ${status.color}` }}
                data-testid="billing-status-pill"
              >
                {data.billing_status === "active" ? <CheckCircle2 size={10} /> : data.billing_status === "grandfathered" ? <Sparkles size={10} /> : <AlertCircle size={10} />}
                {status.label}
              </span>
              <span className="text-xs" style={{ color: "var(--muted)" }}>{status.hint}</span>
            </div>
            <h2 className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>{data.org_name}</h2>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-[0.6rem] uppercase tracking-[0.16em]" style={{ color: "var(--muted)" }}>Plan</div>
                <div style={{ color: "var(--ink)" }}>{data.plan_label} · ${data.price_usd}/mo</div>
              </div>
              <div>
                <div className="text-[0.6rem] uppercase tracking-[0.16em]" style={{ color: "var(--muted)" }}>Directory</div>
                <div style={{ color: overage ? "var(--terracotta)" : "var(--ink)" }} data-testid="billing-current-members">
                  {data.current_members} / {data.max_members} profiles
                </div>
              </div>
              <div>
                <div className="text-[0.6rem] uppercase tracking-[0.16em]" style={{ color: "var(--muted)" }}>Tools</div>
                <div style={{ color: "var(--ink)" }} data-testid="billing-active-tools">
                  {data.tools_enabled.length} enabled
                </div>
              </div>
              <div>
                <div className="text-[0.6rem] uppercase tracking-[0.16em]" style={{ color: "var(--muted)" }}>Renews</div>
                <div style={{ color: "var(--ink)" }}>{fmtDate(data.current_period_end)}</div>
              </div>
            </div>
            {overage && (
              <div className="mt-3 text-xs p-2 rounded-md" style={{ background: "var(--surface-hover)", color: "var(--terracotta)", border: "1px solid var(--terracotta)" }} data-testid="billing-overage">
                You're above your tier limit. Upgrade below so new profiles can keep being added.
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            {hasActiveSub && (
              <button onClick={openPortal} disabled={busy} className="btn-ghost" data-testid="billing-portal-btn">
                <ExternalLink size={14} /> Manage card / cancel
              </button>
            )}
            <button onClick={load} className="btn-ghost text-xs" data-testid="billing-refresh-btn">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        </div>
      </section>

      {/* Tier picker */}
      <section className="mb-8" data-testid="billing-tier-picker">
        <div className="overline mb-3">Step 1 · Pick your tier</div>
        <p className="text-xs mb-3" style={{ color: "var(--muted)" }}>
          The tier caps your directory size and sets each tool's monthly price. Bigger churches pay more per tool.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {data.catalog.map((c) => {
            const isSelected = c.key === selectedTier;
            const isCurrent = c.key === data.plan_tier;
            return (
              <button
                key={c.key}
                onClick={() => setSelectedTier(c.key)}
                className="p-4 rounded-md text-left transition-colors"
                style={{
                  background: isSelected ? "var(--bg-alt)" : "var(--surface)",
                  border: `1px solid ${isSelected ? "var(--gold)" : "var(--border-subtle)"}`,
                }}
                data-testid={`billing-tier-${c.key}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-serif-display text-lg" style={{ color: "var(--ink)" }}>{c.label}</h3>
                  {isCurrent && (
                    <span className="text-[0.5rem] uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ background: "var(--gold)", color: "var(--bg-base)" }}>Now</span>
                  )}
                </div>
                <div className="text-xs" style={{ color: "var(--muted)" }}>{c.member_range} profiles</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Tool picker */}
      <section className="mb-8" data-testid="billing-tool-picker">
        <div className="overline mb-3">Step 2 · Pick your tools</div>
        <p className="text-xs mb-4" style={{ color: "var(--muted)" }}>
          Toggle only what you need. Minimum one tool. Prices update instantly.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.tool_catalog.map((tool) => {
            const Icon = TOOL_ICON[tool.key] || Sparkles;
            const required = !!tool.required;
            const isOn = required || selectedTools.includes(tool.key);
            const price = selectedTier ? tool.prices[selectedTier] : tool.prices.tier_1;
            return (
              <label
                key={tool.key}
                className={`p-4 rounded-md flex items-start gap-3 transition-colors ${required ? "cursor-default" : "cursor-pointer"}`}
                style={{
                  background: isOn ? "var(--bg-alt)" : "var(--surface)",
                  border: `1px solid ${required ? "var(--gold)" : isOn ? "var(--ember)" : "var(--border-subtle)"}`,
                  opacity: required ? 0.95 : 1,
                }}
                data-testid={`billing-tool-${tool.key}`}
              >
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0"
                  style={{ accentColor: required ? "var(--gold)" : "var(--ember)" }}
                  checked={isOn}
                  onChange={() => toggleTool(tool.key)}
                  disabled={required}
                  data-testid={`billing-tool-checkbox-${tool.key}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Icon size={16} style={{ color: required ? "var(--gold)" : isOn ? "var(--ember)" : "var(--muted)" }} />
                    <h3 className="font-serif-display text-lg leading-none" style={{ color: "var(--ink)" }}>{tool.label}</h3>
                    {required && (
                      <span
                        className="text-[0.55rem] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full"
                        style={{ background: "var(--gold)", color: "var(--bg-base)" }}
                        data-testid={`billing-tool-required-badge-${tool.key}`}
                      >
                        Required
                      </span>
                    )}
                  </div>
                  <p className="text-xs mb-2" style={{ color: "var(--muted)" }}>{tool.blurb}</p>
                  <div className="font-serif-display" style={{ color: required ? "var(--gold)" : "var(--ember)" }}>
                    ${price}<span className="text-xs" style={{ color: "var(--muted)" }}> / month</span>
                  </div>
                </div>
              </label>
            );
          })}
        </div>
        <div className="text-[0.7rem] mt-3 italic" style={{ color: "var(--muted)" }}>
          The Bible Study Tracker is the required base — every subscription includes it. Add-on tools are priced by your Tracker tier.
        </div>
      </section>

      {/* Summary + CTA */}
      <section className="card-surface p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="billing-summary">
        <div>
          <div className="overline mb-1">Total</div>
          <div className="font-serif-display text-3xl" style={{ color: "var(--ink)" }}>
            ${totalPrice}<span className="text-sm" style={{ color: "var(--muted)" }}> / month</span>
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            {selectedTools.length === 0 ? "Pick at least one tool to continue." :
              `${selectedTools.length} tool${selectedTools.length === 1 ? "" : "s"} at ${(data.catalog.find((c) => c.key === selectedTier) || {}).label}. Auto-renews monthly.`}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {dirty && (
            <button
              onClick={() => { setSelectedTier(data.plan_tier); setSelectedTools(data.tools_enabled || []); }}
              className="btn-ghost"
              data-testid="billing-revert-btn"
            >
              Revert
            </button>
          )}
          {hasActiveSub ? (
            <button
              onClick={saveChanges}
              disabled={busy || !dirty || selectedTools.length === 0}
              className="btn-primary"
              data-testid="billing-save-btn"
            >
              <CreditCard size={14} /> {busy ? "Saving…" : "Save changes"}
            </button>
          ) : (
            <button
              onClick={startCheckout}
              disabled={busy || selectedTools.length === 0}
              className="btn-primary"
              data-testid="billing-checkout-btn"
            >
              <CreditCard size={14} /> {busy ? "…" : `Start subscription · $${totalPrice}/mo`}
            </button>
          )}
        </div>
      </section>

      {data.billing_status === "grandfathered" && (
        <p className="mt-4 text-xs italic" style={{ color: "var(--muted)" }}>
          Your church is currently comped. Saving here will start real billing on your card.
        </p>
      )}

      <div className="mt-8 text-xs" style={{ color: "var(--muted)" }}>
        Questions? Head to <Link to="/crm/help" style={{ color: "var(--ember)" }} className="hover:underline">Help</Link> or email <a href="mailto:thedisciplelifems@gmail.com" style={{ color: "var(--ember)" }} className="hover:underline">thedisciplelifems@gmail.com</a>.
      </div>
    </div>
  );
}
